/**
 * Field Type Utilities
 * Shared utilities for handling different field types across template components.
 * Centralized to ensure consistency between Designer, Renderer, and Preview.
 */

/**
 * Field types that have their label embedded within the input element itself.
 * These types should not have an additional outer label wrapper.
 * 
 * Examples:
 * - checkbox: Label is part of the checkbox component
 * - toggle: Label is beside the toggle switch
 * - radio: Label is part of the radio button group
 */
export const INLINE_LABEL_FIELD_TYPES = ['checkbox', 'toggle', 'radio'];

/**
 * Field types that are display-only / layout elements.
 * These do not collect user input and should not show a label wrapper.
 */
export const DISPLAY_ONLY_FIELD_TYPES = [
    'heading', 'divider', 'paragraph', 'spacer', 'markdown-block',
    'question-block', 'icon-display', 'image-display', 'info-alert', 'computed-value'
];

/**
 * Helper function to check if a field type has an inline label.
 * 
 * @param {string} fieldType - The field type to check
 * @returns {boolean} True if the field type has an inline label
 * 
 * @example
 * hasInlineLabel('checkbox') // returns true
 * hasInlineLabel('text') // returns false
 * hasInlineLabel('Checkbox') // returns true (case-insensitive)
 * hasInlineLabel(' toggle ') // returns true (handles whitespace)
 */
export const hasInlineLabel = (fieldType) => {
    if (!fieldType) return false;
    const normalized = fieldType.toLowerCase().trim();
    return INLINE_LABEL_FIELD_TYPES.includes(normalized);
};

/**
 * Helper function to check if a field type is display-only (no label wrapper needed).
 * 
 * @param {string} fieldType - The field type to check
 * @returns {boolean} True if the field type is display-only
 */
export const isDisplayOnlyField = (fieldType) => {
    if (!fieldType) return false;
    const normalized = fieldType.toLowerCase().trim();
    return DISPLAY_ONLY_FIELD_TYPES.includes(normalized);
};
